import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { findPreviousClose, get52WeekRange, averageVolume } from './marketutils';
import { unwrapChartResponse } from '../../../utils/unwrapChartResponse';

export const useStockData = () => {
  // ---------------------------------------------------------
  // 1. CORRECT REDUX SELECTORS BASED ON REAL DATA SHAPE
  // ---------------------------------------------------------
  const marketSnapshot = useSelector((s) => s.marketSnapshot || {});
  const openClose = useSelector((s) => s.openClose || {});
  const stockData = useSelector((s) => s.stockData || {});

  // Core sources
  const snapshot = marketSnapshot.singleSnapshot || {};          // <-- contains open_price, prev_close, last_price
  const stock = snapshot?.stock || {};

  // Price bars from Polygon (daily aggregated candles). Mirrors
  // StockChart.jsx's own 3-level fallback chain (see unwrapChartResponse) --
  // this selector used to do a single, non-defensive `stockData?.data?.data`
  // unwrap with nothing to catch a response-shape drift (the same
  // anti-pattern class that caused the sparkline flicker bug, on the app's
  // highest-traffic stock-detail surface).
  const dailyPriceData =
    useSelector((s) => s.dailyPriceData) || unwrapChartResponse(stockData);

  // SAFE fallback from your /stocks_charts API → 1st daily bar
  const firstDaily =
    Array.isArray(dailyPriceData) && dailyPriceData.length > 0
      ? dailyPriceData[dailyPriceData.length - 1]
      : null;


  // OpenClose endpoint data -- the real single-day OHLC/volume fallback,
  // used below for open/high/low/volume once the live snapshot has nothing.
  const openCloseData = openClose.data || {};
  const marketStatus = openCloseData?.market_status;

  // Polygon Ticker Details -- lives under `state.openClose` (mounted from
  // polygonReducer.js in indexRedux.js), not a separate `state.polygon` key,
  // which doesn't exist in the store at all.
  const tickerDetails =
    openClose.tickerDetails?.[0] ||
    openClose.tickerDetails ||
    {};

  // Financials -- GET_FINANCIALS_DATA (polygonReducer.js) stores this as an
  // object keyed by ticker (`{ AAPL: [...] }`), not a flat array, so it must
  // be indexed by ticker first. Explicitly picks the `ttm` row rather than
  // trusting array order, since the API's own row ordering isn't a
  // documented contract.
  const currentTicker = snapshot?.ticker || openCloseData?.symbol;
  const financialsForTicker = currentTicker ? openClose.financials?.[currentTicker] : null;
  const financials =
    Array.isArray(financialsForTicker)
      ? financialsForTicker.find((f) => f.timeframe === 'ttm') || financialsForTicker[0] || {}
      : {};

  // Smart previous close logic (uses historical bars)
  const smartPreviousClose = findPreviousClose(dailyPriceData);

  // ---------------------------------------------------------
  // 2. DERIVED DATA / CALCULATIONS (CORRECT SOURCES)
  // ---------------------------------------------------------
  const data = useMemo(() => {
    // ----- PRICE -----
    // --- OPEN (snapshot → real open-close fetch → daily bars → null) ---
    const open =
      snapshot?.open_price ??
      openCloseData?.open ??
      firstDaily?.period_open ??
      null;

    // --- PREVIOUS CLOSE (smart → snapshot → daily close) ---
    const previousClose =
      smartPreviousClose ??
      snapshot?.prev_close ??
      firstDaily?.period_close ??
      null;

    // --- CURRENT PRICE ---
    const currentPrice =
      snapshot?.last_price ??
      openCloseData?.close ??
      firstDaily?.period_close ??
      null;

    // --- HIGH ---
    const high =
      openCloseData?.high ??
      firstDaily?.period_high ??
      null;

    // --- LOW ---
    const low =
      openCloseData?.low ??
      firstDaily?.period_low ??
      null;

    // --- VOLUME ---
    const volume =
      snapshot?.volume ??
      openCloseData?.volume ??
      firstDaily?.total_volume ??
      null;


    const change =
      snapshot?.change ??
      (currentPrice && previousClose
        ? currentPrice - previousClose
        : null);

    const changePercent =
      snapshot?.change_percent ??
      (change && previousClose
        ? (change / previousClose) * 100
        : null);

    // ----- IDENTITY -----
    const symbol =
      snapshot?.ticker ||
      openCloseData?.symbol ||
      '--';

    const companyName =
      stock?.name ||
      tickerDetails?.name ||
      '--';

    const description =
      stock?.description ||
      tickerDetails?.description ||
      null;

    const homepage =
      stock?.homepage_url ||
      tickerDetails?.homepage_url ||
      null;

    // ----- MARKET METRICS -----
    let marketCap =
      stock?.market_cap ??
      tickerDetails?.market_cap ??
      null;

    // Compute fallback market cap manually using best-available price source
    if (marketCap === null) {
      const shares =
        tickerDetails?.share_class_shares_outstanding ??
        tickerDetails?.weighted_shares_outstanding ??
        stock?.shares_outstanding ??
        null;

      // lastDailyClose defined above
      const lastDailyClose = Array.isArray(dailyPriceData)
        ? dailyPriceData[dailyPriceData.length - 1]?.close ??
          dailyPriceData[dailyPriceData.length - 1]?.period_close ??
          null
        : null;

      const price =
        snapshot?.last_price ??
        openCloseData?.close ??
        lastDailyClose ??
        null;

      if (shares && price) {
        marketCap = shares * price;
      }
    }



    const sharesOutstanding =
      stock?.shares_outstanding ??
      tickerDetails?.share_class_shares_outstanding ??
      tickerDetails?.weighted_shares_outstanding ??
      null;

    // ----- FINANCIALS (FULLY FIXED) -----
    const revenue = financials?.revenue ?? null;
    const netIncome = financials?.net_income ?? null;
    const grossProfit = financials?.gross_profit ?? null;
    const operatingIncome = financials?.operating_income ?? null;

    const assets = financials?.assets ?? null;
    const liabilities = financials?.liabilities ?? null;
    const equity = financials?.equity ?? null;

    const currentAssets = financials?.current_assets ?? null;
    const currentLiabilities = financials?.current_liabilities ?? null;

    // ----- COMPANY META -----
    const totalEmployees =
      stock?.total_employees ??
      tickerDetails?.total_employees ??
      null;

    const listDate =
      stock?.list_date ??
      tickerDetails?.list_date ??
      null;

    const primaryExchange =
      stock?.primary_exchange ??
      tickerDetails?.primary_exchange ??
      null;

    const sicCode =
      stock?.sic_code ??
      tickerDetails?.sic_code ??
      null;

    const sicDescription =
      stock?.sic_description ??
      tickerDetails?.sic_description ??
      null;

    const address =
      stock?.address ??
      tickerDetails?.address ??
      null;

    // ----- HISTORY -----
    const { high: weekHigh52, low: weekLow52 } =
      get52WeekRange(dailyPriceData);

    const avgVolume30 = averageVolume(dailyPriceData, 30);
    const avgVolume10 = averageVolume(dailyPriceData, 10);

    // ----- RATIOS -----
    const peRatio =
      netIncome &&
      sharesOutstanding &&
      currentPrice &&
      netIncome > 0
        ? (currentPrice * sharesOutstanding) / netIncome
        : null;

    const eps =
      netIncome &&
      sharesOutstanding
        ? netIncome / sharesOutstanding
        : null;

    const bookValue =
      equity &&
      sharesOutstanding &&
      equity > 0
        ? equity / sharesOutstanding
        : null;

    const priceToBook =
      currentPrice &&
      bookValue &&
      bookValue > 0
        ? currentPrice / bookValue
        : null;

    const currentRatio =
      currentAssets &&
      currentLiabilities &&
      currentLiabilities > 0
        ? currentAssets / currentLiabilities
        : null;

    const debtToEquity =
      liabilities &&
      equity &&
      equity > 0
        ? liabilities / equity
        : null;

    const grossMargin =
      grossProfit &&
      revenue &&
      revenue > 0
        ? (grossProfit / revenue) * 100
        : null;

    const operatingMargin =
      operatingIncome &&
      revenue &&
      revenue > 0
        ? (operatingIncome / revenue) * 100
        : null;

    const netMargin =
      netIncome &&
      revenue &&
      revenue > 0
        ? (netIncome / revenue) * 100
        : null;

    const roe =
      netIncome &&
      equity &&
      equity > 0
        ? (netIncome / equity) * 100
        : null;

    const roa =
      netIncome &&
      assets &&
      assets > 0
        ? (netIncome / assets) * 100
        : null;

    const priceToSales =
      revenue &&
      sharesOutstanding &&
      currentPrice &&
      revenue > 0 &&
      sharesOutstanding > 0
        ? (currentPrice * sharesOutstanding) / revenue
        : null;

    const earningsYield =
      peRatio && peRatio > 0
        ? (1 / peRatio) * 100
        : null;

    // ----- RETURN -----
    return {
      symbol,
      companyName,
      description,
      homepage,

      currentPrice,
      open,
      high,
      low,
      volume,
      previousClose,
      change,
      changePercent,

      marketCap,
      sharesOutstanding,

      weekHigh52,
      weekLow52,
      avgVolume30,
      avgVolume10,

      revenue,
      netIncome,
      grossProfit,
      operatingIncome,
      assets,
      liabilities,
      equity,
      currentAssets,
      currentLiabilities,

      totalEmployees,
      listDate,
      primaryExchange,
      sicCode,
      sicDescription,
      address,

      peRatio,
      eps,
      bookValue,
      priceToBook,
      currentRatio,
      debtToEquity,
      grossMargin,
      operatingMargin,
      netMargin,
      roe,
      roa,
      priceToSales,
      earningsYield,
    };
  }, [
    snapshot,
    openCloseData,
    stock,
    tickerDetails,
    financials,
    dailyPriceData,
    firstDaily,
    smartPreviousClose,
    marketStatus,
  ]);

  return { data, openCloseData, marketStatus, dailyPriceData };
};
